import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, Clock, RefreshCw, Video, FileText, StickyNote } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type ItemProgress = {
  item_id: string;
  content_id: string;
  title: string;
  type: 'video' | 'article' | 'note';
  is_required: boolean;
  position: number;
  started_at: string | null;
  completed_at: string | null;
  watched_seconds: number;
  duration_seconds: number | null;
  progress_pct: number;
  status: 'completed' | 'in_progress' | 'not_started';
};

type PlayerProgress = {
  user_id: string;
  username: string;
  quiz_complete: boolean;
  items: ItemProgress[];
};

type Report = {
  lesson_id: string;
  team_id: string;
  is_custom: boolean;
  items: { item_id: string }[];
  players: PlayerProgress[];
};

const TYPE_ICON = { video: Video, article: FileText, note: StickyNote } as const;

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

/**
 * What the coach actually needs: for every player on the team, the lesson-level
 * status AND the status of each individual content item, straight from the
 * persisted progress rows. Nothing here is inferred client-side.
 */
export function LessonProgressPanel({ lessonId, teamId }: { lessonId: string; teamId: string }) {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_lesson_team_progress' as any, {
      p_lesson_id: lessonId,
      p_team_id: teamId,
    });
    if (!error && data) setReport(data as unknown as Report);
    setLoading(false);
  }, [lessonId, teamId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  if (loading) return <p className="text-[11px] text-muted-foreground py-2">Loading player progress…</p>;
  if (!report) return <p className="text-[11px] text-muted-foreground py-2">Progress unavailable.</p>;

  const players = report.players ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground font-display font-bold uppercase tracking-wider">
          Player progress
        </p>
        <button onClick={load} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {players.length === 0 && (
        <p className="text-[11px] text-muted-foreground py-1">No players on this team yet.</p>
      )}

      {players.map((p) => {
        const items = p.items ?? [];
        const requiredItems = items.filter((i) => i.is_required);
        const requiredDone = requiredItems.filter((i) => i.status === 'completed').length;
        const anyActivity = items.some((i) => i.status !== 'not_started') || p.quiz_complete;
        // Custom lessons have no quiz: required content is the whole lesson.
        const lessonComplete = report.is_custom
          ? (items.length > 0 ? requiredDone === requiredItems.length && requiredItems.length > 0 : p.quiz_complete)
          : p.quiz_complete && requiredDone === requiredItems.length;
        const totalUnits = items.length + (report.is_custom ? 0 : 1);
        const doneUnits =
          items.filter((i) => i.status === 'completed').length + (!report.is_custom && p.quiz_complete ? 1 : 0);
        const pct = totalUnits > 0 ? Math.round((doneUnits / totalUnits) * 100) : lessonComplete ? 100 : 0;
        const completionDate = lessonComplete
          ? items.reduce<string | null>((acc, i) => (i.completed_at && (!acc || i.completed_at > acc) ? i.completed_at : acc), null)
          : null;

        return (
          <div key={p.user_id} className="rounded-lg border border-border bg-white/[0.02] px-2.5 py-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-display font-bold truncate flex-1">{p.username}</span>
              <span
                className={`text-[9px] font-display font-bold px-1.5 py-0.5 rounded ${
                  lessonComplete
                    ? 'bg-emerald-500/15 text-emerald-400'
                    : anyActivity
                      ? 'bg-amber-500/15 text-amber-400'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {lessonComplete ? 'Completed' : anyActivity ? 'In Progress' : 'Not Started'}
              </span>
              <span className="text-[10px] text-muted-foreground font-display font-bold shrink-0">{pct}%</span>
            </div>

            {completionDate && (
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" /> Completed {fmtDate(completionDate)}
              </p>
            )}

            {items.length === 0 ? (
              <p className="text-[10px] text-muted-foreground">
                No content in this lesson{report.is_custom ? '' : ' — quiz only'}.
              </p>
            ) : (
              <div className="space-y-1">
                {items.map((i) => {
                  const Icon = TYPE_ICON[i.type] ?? FileText;
                  return (
                    <div key={i.item_id} className="flex items-center gap-2 pl-1">
                      {i.status === 'completed' ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      ) : i.status === 'in_progress' ? (
                        <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      ) : (
                        <Circle className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                      )}
                      <Icon className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="text-[11px] truncate flex-1">{i.title}</span>
                      {!i.is_required && (
                        <span className="text-[9px] text-muted-foreground shrink-0">optional</span>
                      )}
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {i.status === 'not_started' ? 'Not started' : `${i.progress_pct}%`}
                      </span>
                      {i.completed_at && (
                        <span className="text-[9px] text-muted-foreground shrink-0 hidden sm:inline">
                          {fmtDate(i.completed_at)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!report.is_custom && (
              <div className="flex items-center gap-2 pl-1">
                {p.quiz_complete ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                ) : (
                  <Circle className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                )}
                <span className="text-[11px] flex-1">Quiz</span>
                <span className="text-[10px] text-muted-foreground">{p.quiz_complete ? '100%' : 'Not started'}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
